import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { learningService } from "../../api/learningService";
import Tabs from "../../components/ui/Tabs";
import { getErrorMessage } from "../../utils/apiError";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import {
  getYearOptions,
  LEARNING_KEY,
  PARTICIPANT_STATUS,
} from "./learningUtils";
import SeminarCard from "./components/SeminarCard";
import SeminarDetailDrawer from "./components/SeminarDetailDrawer";
import LearningRecord from "./components/LearningRecord";
import MyBonds, { useMyBonds } from "./components/MyBonds";
import ui from "../../components/ui/ui.module.css";
import styles from "./Learning.module.css";

const SEMINAR_TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "all", label: "All" },
];

const toArray = (data) => (Array.isArray(data) ? data : []);

// Loading / error / empty, or the cards.
const SeminarGrid = ({ query, emptyMessage, onOpen }) => {
  const seminars = toArray(query.data);

  if (query.isLoading) {
    return (
      <div className={ui.empty}>
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        Loading seminars…
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className={cx(ui.alert, ui.alertDanger, "m-3")}>
        {getErrorMessage(query.error, "Unable to load seminars.")}
      </div>
    );
  }
  if (!seminars.length) return <div className={ui.empty}>{emptyMessage}</div>;

  return (
    <div className={styles.cardList}>
      {seminars.map((seminar) => (
        <SeminarCard key={seminar.id} seminar={seminar} onOpen={onOpen} />
      ))}
    </div>
  );
};

const MyLearningView = () => {
  const hasEmployee = !!getEmployeeId();
  const [section, setSection] = useState("mine"); // "mine" | "available" | "record" | "bonds"
  const [tab, setTab] = useState("upcoming");
  const [status, setStatus] = useState("");
  const [year, setYear] = useState("");
  const [selected, setSelected] = useState(null);

  const filters = { tab, status, year };
  const mineQuery = useQuery({
    queryKey: [LEARNING_KEY, "my", filters],
    queryFn: () => learningService.getMySeminars(filters),
    enabled: hasEmployee && section === "mine",
  });

  // Always loaded so the tab can show how many are open.
  const availableQuery = useQuery({
    queryKey: [LEARNING_KEY, "available"],
    queryFn: learningService.getAvailable,
    enabled: hasEmployee,
  });

  const bondsQuery = useMyBonds(hasEmployee);
  const bondCount = toArray(bondsQuery.data).length;

  if (!hasEmployee) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        Your account is not linked to an employee record, so learning is not
        available.
      </div>
    );
  }

  const sections = [
    { id: "mine", label: "My seminars" },
    {
      id: "available",
      label: "Open for registration",
      count: toArray(availableQuery.data).length,
    },
    { id: "record", label: "Skills & record" },
    ...(bondCount > 0
      ? [{ id: "bonds", label: "My bonds", count: bondCount }]
      : []),
  ];

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h4 className="page-title">My Learning</h4>
          <p className="page-subtitle">
            Seminars and trainings you are part of, and the skills you have
            earned.
          </p>
        </div>
      </div>

      <Tabs tabs={sections} active={section} onChange={setSection} />

      {section === "mine" && (
        <div className={ui.card}>
          <div className={styles.toolbar}>
            <Tabs
              tabs={SEMINAR_TABS}
              active={tab}
              onChange={setTab}
              variant="pills"
            />
            <div className={ui.actions}>
              <select
                className={ui.input}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="My status"
              >
                <option value="">Any status</option>
                {Object.entries(PARTICIPANT_STATUS).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </select>
              <select
                className={ui.input}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                aria-label="Year"
              >
                <option value="">All years</option>
                {getYearOptions().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <SeminarGrid
            query={mineQuery}
            onOpen={setSelected}
            emptyMessage={
              tab === "upcoming"
                ? "No upcoming seminars. Check “Open for registration” for seminars you can join."
                : "No seminars found."
            }
          />
        </div>
      )}

      {section === "available" && (
        <div className={ui.card}>
          <div className={ui.cardHeader}>
            <span className={ui.cardTitle}>Open for registration</span>
            <span className={ui.hint}>
              You can register until the seminar ends, and withdraw until it
              starts.
            </span>
          </div>
          <SeminarGrid
            query={availableQuery}
            onOpen={setSelected}
            emptyMessage="No open seminars right now."
          />
        </div>
      )}

      {section === "record" && <LearningRecord onOpenSeminar={setSelected} />}
      {section === "bonds" && <MyBonds />}

      {selected && (
        <SeminarDetailDrawer
          seminar={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default MyLearningView;
